import { useState, useEffect, useCallback } from "react";
import staticService from "../../services/StaticService";
import {
  useScrollService,
  NavigationUtils,
} from "../../services/scrollService"; // ✅ updated import
import { CarouselSlide, NavigationIndicators } from "../../components";
import "./Carousel.css";

const Carousel = ({
  mediaItems = [],
  errorMessage = null,
  sensitivity = 60,
  transitionDuration = 400,
  debounceDelay = 80,
  momentum = true,
  snapThreshold = 0.3,
  allowVerticalWheelPassthrough = false,
  showIndicators = true,
  indicatorProps = {},
  className = "",
  audioEnabled = false,
  onCurrentItemChange = null,
  onCurrentItemDisplayReady = null,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    setCurrentIndex(0);
  }, [mediaItems]);

  // visible items
  const getVisibleItems = () => {
    if (!mediaItems || mediaItems.length === 0) return [];
    const visibleItems = NavigationUtils.getVisibleItems(
      mediaItems,
      currentIndex
    );
    return visibleItems.map((item) => ({
      ...item,
      url:
        item.url &&
        (item.url.startsWith("http")
          ? item.url
          : `${staticService.API_BASE_URL}${item.url}`),
      thumbnail_url: item.thumbnail_url
        ? item.thumbnail_url.startsWith("http")
          ? item.thumbnail_url
          : `${staticService.API_BASE_URL}${item.thumbnail_url}`
        : null,
    }));
  };
  // navigation handler
  const handleNavigation = useCallback(
    (direction, force = 1) => {
      setIsTransitioning(true);
      const nextIndex = NavigationUtils.getNextIndex(
        currentIndex,
        mediaItems.length,
        direction
      );
      setCurrentIndex(nextIndex);

      setTimeout(() => {
        setIsTransitioning(false);
      }, transitionDuration);
    },
    [currentIndex, mediaItems.length, transitionDuration]
  );

  // ✅ useScrollService instead of useHorizontalScroll
  const { containerRef, isScrollingRef, navigateTo } = useScrollService(
    handleNavigation,
    {
      direction: "horizontal",
      sensitivity,
      transitionDuration,
      debounceDelay,
      momentum,
      snapThreshold,
      allowVerticalWheelPassthrough,
    },
    mediaItems.length
  );

  // indicator click
  const handleIndicatorClick = useCallback(
    (index) => {
      if (isScrollingRef.current || index === currentIndex) return;

      setIsTransitioning(true);
      setCurrentIndex(index);

      setTimeout(() => {
        setIsTransitioning(false);
      }, transitionDuration);
    },
    [currentIndex, transitionDuration, isScrollingRef]
  );

  // expose API
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.navigateTo = navigateTo;
      containerRef.current.goToIndex = (index) => handleIndicatorClick(index);
      containerRef.current.getCurrentIndex = () => currentIndex;
    }
  }, [containerRef, navigateTo, handleIndicatorClick, currentIndex]);

  useEffect(() => {
    if (onCurrentItemChange) {
      onCurrentItemChange(mediaItems[currentIndex] || null, currentIndex);
    }
  }, [currentIndex, mediaItems, onCurrentItemChange]);

  if (errorMessage) {
    return (
      <div className={`carousel-container error ${className}`}>
        <div className="error-message">
          Failed to load carousel: {errorMessage}
        </div>
      </div>
    );
  }

  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div className={`carousel-container empty ${className}`}>
        No media available
      </div>
    );
  }

  const visibleItems = getVisibleItems();

  return (
    <div
      ref={containerRef}
      className={`carousel-container ${
        isTransitioning ? "transitioning" : ""
      } ${className}`}
      style={NavigationUtils.getContainerStyles(
        transitionDuration,
        "horizontal"
      )}
    >
      <div className="carousel-content">
        {visibleItems.map((item) => (
          <CarouselSlide
            key={`${item.position}-${item.position_id ?? item.index ?? item.url}`}
            item={item}
            position={item.position}
            audioEnabled={audioEnabled}
            onDisplayReady={
              item.position === "current" ? onCurrentItemDisplayReady : null
            }
          />
        ))}
      </div>

      {showIndicators && (
        <NavigationIndicators
          items={mediaItems}
          currentIndex={currentIndex}
          onIndicatorClick={handleIndicatorClick}
          isScrolling={isScrollingRef.current || isTransitioning}
          {...indicatorProps}
        />
      )}
    </div>
  );
};

export default Carousel;
